/**
 * LLM Token Usage - view usage records and summary for a bot
 * Route: /bots/:botId/usage
 * Guard: user must have access to bot via tenantBots
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { useBotRoute } from '../lib/useBotRoute';
import { botPath } from '../lib/botNav';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { WalletCard } from '../components/usage/WalletCard';
import {
  hubService,
  type LlmTokenUsageEntry,
  type LlmUsageSummaryRow,
} from '../services/hub.service';


export function Usage() {
  const { system, botId } = useBotRoute();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { tenants, tenantBots } = useTenant();
  const [summary, setSummary] = useState<LlmUsageSummaryRow[]>([]);
  const [items, setItems] = useState<LlmTokenUsageEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const tenant = tenants?.find((t) => {
    const bots = tenantBots?.[t.id] ?? [];
    return bots.some((b) => b.botId === botId);
  });
  const bots = tenant ? tenantBots?.[tenant.id] ?? [] : [];
  const hasAccess = bots.some((b) => b.botId === botId);
  const bot = bots.find((b) => b.botId === botId);

  useEffect(() => {
    if (!tenant || !hasAccess) {
      navigate('/');
    }
  }, [tenant, hasAccess, navigate]);

  const load = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const toDate = to || new Date().toISOString().slice(0, 10);
      const fromDate = from || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const [summaryData, listData] = await Promise.all([
        hubService.getLlmUsageSummary(
          { system: system, botId, from: fromDate, to: toDate },
          token,
        ),
        hubService.listLlmUsage(
          { system: system, botId, from: fromDate, to: toDate, limit: 100 },
          token,
        ),
      ]);
      setSummary(summaryData);
      setItems(listData.items);
      setTotal(listData.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [botId, from, to, getToken]);

  useEffect(() => {
    if (botId && hasAccess) {
      load();
    }
  }, [botId, hasAccess, load]);

  const totalRequest = summary.reduce((s, r) => s + r.requestTokens, 0);
  const totalResponse = summary.reduce((s, r) => s + r.responseTokens, 0);
  const totalTokens = summary.reduce((s, r) => s + r.totalTokens, 0);

  if (!tenant || !hasAccess || !botId) {
    return null;
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to={botPath({ system, botId }, 'config')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Config
          </Link>
          <Link
            to={botPath({ system, botId }, 'cache')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Cache
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            LLM Token Usage · {bot?.botName || botId}
          </h1>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        {hasAccess && <WalletCard system={system} botId={botId} token={getToken()} />}

        <div className="mb-6 flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Apply'}
          </button>
        </div>

        {loading && !summary.length ? (
          <div className="text-gray-500">Loading usage…</div>
        ) : (
          <>
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Summary by day
              </h2>
              <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Requests
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Request tokens
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Response tokens
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Total tokens
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {summary.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-4 text-sm text-gray-500">
                          No usage in this range.
                        </td>
                      </tr>
                    ) : (
                      summary.map((row) => (
                        <tr key={`${row.system}-${row.botId}-${row.date}`}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {row.date}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {row.count}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {row.requestTokens.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {row.responseTokens.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {row.totalTokens.toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {summary.length > 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  Total in range: {totalRequest.toLocaleString()} request /{' '}
                  {totalResponse.toLocaleString()} response /{' '}
                  {totalTokens.toLocaleString()} total tokens
                </p>
              )}
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Recent records (up to 100)
              </h2>
              <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Time
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Provider
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Source
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Request
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Response
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Total
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Model
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-4 text-sm text-gray-500">
                          No records in this range.
                        </td>
                      </tr>
                    ) : (
                      items.map((row) => (
                        <tr key={row.id}>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {new Date(row.recordedAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {row.provider ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {row.callType ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {row.source}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {row.requestTokens}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {row.responseTokens}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {row.totalTokens ?? row.requestTokens + row.responseTokens}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {row.model ?? '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {total > items.length && (
                <p className="mt-2 text-sm text-gray-500">
                  Showing {items.length} of {total} records.
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
